import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import {
    Plus,
    SearchLg,
    Edit01,
    Users01,
    UserPlus01,
    Settings01
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { getCompanies } from '@/data/api/companies';
import type { Company, CompanyFilters, CompanyType } from '@/types/company';
import { mapCompanyTypeToDisplay } from '@/types/company';
import { useTranslation } from 'react-i18next';
import { countries } from '@/data/countries';

export const CompaniesListPage = () => {
    const { t } = useTranslation(['pages:companies', 'common']);
    const navigate = useNavigate();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState<boolean | ''>('');
    const [companyTypeFilter, setCompanyTypeFilter] = useState<CompanyType | ''>('');
    const [countryFilter, setCountryFilter] = useState('');
    const isFirstLoad = useRef(true);

    // Debounce search input (300ms)
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const companyTypeOptions = [
        { id: '', label: t('list.filters.allTypes') },
        { id: 'sender', label: t('list.filters.companyTypes.sender') },
        { id: 'receiver', label: t('list.filters.companyTypes.receiver') },
        { id: 'freight_forwarder', label: t('list.filters.companyTypes.freightForwarder') },
        { id: 'carrier', label: t('list.filters.companyTypes.carrier') },
        { id: 'customs_broker', label: t('list.filters.companyTypes.customsBroker') },
    ];

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'true', label: t('list.status.active') },
        { id: 'false', label: t('list.status.inactive') }
    ];

    const countryOptions = [
        { id: '', label: t('list.filters.allCountries') },
        ...countries.map(country => ({
            id: country.id,
            label: country.label
        }))
    ];

    useEffect(() => {
        const loadCompanies = async () => {
            if (isFirstLoad.current) {
                setIsLoading(true);
            }
            try {
                const filters: CompanyFilters = {
                    search: debouncedSearch || undefined,
                    is_active: isActiveFilter !== '' ? isActiveFilter : undefined,
                    company_type: companyTypeFilter || undefined,
                    country_code: countryFilter || undefined,
                    limit: 100
                };

                const data = await getCompanies(filters);
                setCompanies(data);
            } catch (error) {
                console.error('Error loading companies:', error);
                setCompanies([]);
            } finally {
                setIsLoading(false);
                isFirstLoad.current = false;
            }
        };

        loadCompanies();
    }, [debouncedSearch, isActiveFilter, companyTypeFilter, countryFilter]);

    const getStatusBadge = (isActive: boolean, onboardingStatus: string) => {
        if (!isActive) {
            return <Badge color="error" size="sm">{t('list.status.inactive')}</Badge>;
        }
        if (onboardingStatus === 'suspended') {
            return <Badge color="warning" size="sm">{t('list.status.suspended')}</Badge>;
        }
        return <Badge color="success" size="sm">{t('list.status.active')}</Badge>;
    };

    const getCompanyTypeBadge = (type: CompanyType) => {
        const colorMap: Record<CompanyType, 'blue' | 'success' | 'warning' | 'gray'> = {
            sender: 'blue',
            receiver: 'success',
            freight_forwarder: 'warning',
            port_authority: 'gray',
            carrier: 'blue',
            customs_broker: 'gray',
            insurance_provider: 'gray',
        };
        return <Badge color={colorMap[type] || 'gray'} size="sm">{mapCompanyTypeToDisplay(type)}</Badge>;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('list.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <Link
                    to="/admin/companies/new"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <Plus className="size-5" />
                    {t('list.newCompany')}
                </Link>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Input
                        placeholder={t('list.filters.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.filters.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={isActiveFilter === '' ? '' : String(isActiveFilter)}
                        onSelectionChange={(key) => setIsActiveFilter(key === '' ? '' : key === 'true')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.companyTypePlaceholder')}
                        items={companyTypeOptions}
                        selectedKey={companyTypeFilter}
                        onSelectionChange={(key) => setCompanyTypeFilter(key as CompanyType | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.countryPlaceholder')}
                        items={countryOptions}
                        selectedKey={countryFilter}
                        onSelectionChange={(key) => setCountryFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Companies Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('list.table.title')} (${companies.length})`}
                    description={t('list.table.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.table.headers.companyType')} isRowHeader />
                        <Table.Head label={t('list.table.headers.companyName')} />
                        <Table.Head label={t('list.table.headers.legalName')} />
                        <Table.Head label={t('list.table.headers.country')} />
                        <Table.Head label={t('list.table.headers.status')} />
                        <Table.Head label={t('list.table.headers.createdAt')} />
                        <Table.Head label={t('list.table.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {companies.map((company) => (
                            <Table.Row key={company.company_id}>
                                <Table.Cell>
                                    {getCompanyTypeBadge(company.company_type)}
                                </Table.Cell>
                                <Table.Cell>
                                    <div>
                                        <div className="font-medium text-primary">{company.company_name}</div>
                                        {company.primary_email && (
                                            <div className="text-sm text-tertiary">{company.primary_email}</div>
                                        )}
                                        {company.tax_id && (
                                            <div className="text-xs text-quaternary">{company.tax_id}</div>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{company.legal_name || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {company.headquarters_address?.country_code || '-'}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    {getStatusBadge(company.is_active, company.onboarding_status)}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {new Date(company.created_at).toLocaleDateString('it-IT')}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/admin/companies/${company.company_id}/edit`}
                                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            <Edit01 className="size-4" />
                                            {t('list.table.edit')}
                                        </Link>
                                        <Link
                                            to={`/admin/companies/${company.company_id}/team`}
                                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            <UserPlus01 className="size-4" />
                                            {t('list.table.manageTeam')}
                                        </Link>
                                        <Link
                                            to={`/admin/companies/${company.company_id}/menu-config`}
                                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            <Settings01 className="size-4" />
                                            {t('list.table.menuConfig')}
                                        </Link>
                                        <Dropdown.Root>
                                            <Dropdown.DotsButton />
                                            <Dropdown.Popover className="w-min">
                                                <Dropdown.Menu>
                                                    <Dropdown.Item
                                                        icon={Edit01}
                                                        onAction={() => navigate(`/admin/companies/${company.company_id}/edit`)}
                                                    >
                                                        {t('list.table.edit')}
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        icon={UserPlus01}
                                                        onAction={() => navigate(`/admin/companies/${company.company_id}/team`)}
                                                    >
                                                        {t('list.table.manageTeam')}
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        icon={Settings01}
                                                        onAction={() => navigate(`/admin/companies/${company.company_id}/menu-config`)}
                                                    >
                                                        {t('list.table.menuConfig')}
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown.Popover>
                                        </Dropdown.Root>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {companies.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <Users01 className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.table.noCompanies.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || isActiveFilter !== '' || companyTypeFilter || countryFilter
                                ? t('list.table.noCompanies.message')
                                : t('list.table.noCompanies.emptyMessage')
                            }
                        </p>
                    </div>
                    {!searchTerm && isActiveFilter === '' && !companyTypeFilter && !countryFilter && (
                        <Link
                            to="/admin/companies/new"
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                        >
                            <Plus className="size-5" />
                            {t('list.table.noCompanies.createFirst')}
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};

